import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { Verein, Kurs, KursTeilnehmer, User, KursMaterial } from '../models/models';

interface VereinListResponse {
  success: boolean;
  vereine: Verein[];
}

interface VereinDetailsResponse {
  success: boolean;
  verein: Verein;
}

interface VereinCreateResponse {
  success: boolean;
  verein_id: number;
  status: string;
  message: string;
}

interface KursListResponse {
  success: boolean;
  kurse: Kurs[];
}

interface KursDetailsResponse {
  success: boolean;
  kurs: Kurs;
}

interface KursCreateResponse {
  success: boolean;
  kurs_id: number;
  message: string;
}

interface MitgliederResponse {
  success: boolean;
  mitglieder: User[];
}

interface StandardResponse {
  success: boolean;
  message: string;
}

@Injectable({
  providedIn: 'root'
})
export class VereinService {
  private apiUrl = environment.apiUrl;

  // State
  currentVerein = signal<Verein | null>(null);
  vereine = signal<Verein[]>([]);

  constructor(private http: HttpClient) {}

  // ========== VEREINE ==========

  /**
   * Lädt alle Vereine (aktive oder alle für Admin)
   */
  loadVereine(activeOnly = true, pending = false): Observable<VereinListResponse> {
    const params: any = {};
    if (activeOnly) params.active_only = '1';
    if (pending) params.pending = '1';

    return this.http.get<VereinListResponse>(`${this.apiUrl}/vereine/list.php`, { params });
  }

  /**
   * Lädt Details eines Vereins
   */
  getVereinDetails(vereinId: number): Observable<VereinDetailsResponse> {
    return this.http.get<VereinDetailsResponse>(`${this.apiUrl}/vereine/details.php`, {
      params: { id: vereinId.toString() }
    });
  }

  /**
   * Erstellt einen neuen Verein
   */
  createVerein(verein: Partial<Verein>): Observable<VereinCreateResponse> {
    return this.http.post<VereinCreateResponse>(`${this.apiUrl}/vereine/create.php`, verein);
  }

  /**
   * Genehmigt einen Verein (nur ADMIN)
   */
  approveVerein(vereinId: number): Observable<StandardResponse> {
    return this.http.post<StandardResponse>(`${this.apiUrl}/vereine/approve.php`, {
      verein_id: vereinId
    });
  }

  /**
   * Beitrittsanfrage zu einem Verein
   */
  joinVerein(vereinId: number): Observable<StandardResponse> {
    return this.http.post<StandardResponse>(`${this.apiUrl}/vereine/join.php`, {
      verein_id: vereinId
    });
  }

  /**
   * Lädt Mitglieder eines Vereins
   */
  getMitglieder(vereinId: number, pendingOnly = false): Observable<MitgliederResponse> {
    const params: any = { verein_id: vereinId.toString() };
    if (pendingOnly) params.pending = '1';

    return this.http.get<MitgliederResponse>(`${this.apiUrl}/vereine/mitglieder.php`, { params });
  }

  /**
   * Genehmigt oder lehnt Mitglied ab
   */
  approveMember(userId: number, approve: boolean): Observable<StandardResponse> {
    return this.http.post<StandardResponse>(`${this.apiUrl}/vereine/approve-member.php`, {
      user_id: userId,
      approve
    });
  }

  // ========== KURSE ==========

  /**
   * Lädt alle Kurse eines Vereins
   */
  getKurse(vereinId: number): Observable<KursListResponse> {
    return this.http.get<KursListResponse>(`${this.apiUrl}/kurse/list.php`, {
      params: { verein_id: vereinId.toString() }
    });
  }

  /**
   * Lädt Details eines Kurses
   */
  getKursDetails(kursId: number): Observable<KursDetailsResponse> {
    return this.http.get<KursDetailsResponse>(`${this.apiUrl}/kurse/details.php`, {
      params: { id: kursId.toString() }
    });
  }

  /**
   * Erstellt einen neuen Kurs
   */
  createKurs(kurs: Partial<Kurs>): Observable<KursCreateResponse> {
    return this.http.post<KursCreateResponse>(`${this.apiUrl}/kurse/create.php`, kurs);
  }

  /**
   * Fügt Teilnehmer zu Kurs hinzu
   */
  addTeilnehmer(kursId: number, userId: number): Observable<StandardResponse> {
    return this.http.post<StandardResponse>(`${this.apiUrl}/kurse/add-teilnehmer.php`, {
      kurs_id: kursId,
      user_id: userId
    });
  }

  /**
   * Entfernt Teilnehmer aus Kurs
   */
  removeTeilnehmer(kursId: number, userId: number): Observable<StandardResponse> {
    return this.http.post<StandardResponse>(`${this.apiUrl}/kurse/remove-teilnehmer.php`, {
      kurs_id: kursId,
      user_id: userId
    });
  }

  // ========== MATERIALIEN ==========

  /**
   * Lädt Materialien eines Kurses
   */
  getMaterialien(kursId: number): Observable<{ success: boolean; materialien: KursMaterial[] }> {
    return this.http.get<{ success: boolean; materialien: KursMaterial[] }>(
      `${this.apiUrl}/kurse/material-list.php`,
      { params: { kurs_id: kursId.toString() } }
    );
  }

  /**
   * Lädt ein Material hoch
   */
  uploadMaterial(kursId: number, formData: FormData): Observable<StandardResponse> {
    formData.append('kurs_id', kursId.toString());
    return this.http.post<StandardResponse>(`${this.apiUrl}/kurse/material-upload.php`, formData);
  }

  /**
   * Download-URL für Material
   */
  getMaterialDownloadUrl(materialId: number): string {
    return `${this.apiUrl}/kurse/material-download.php?id=${materialId}`;
  }

  /**
   * Löscht ein Material
   */
  deleteMaterial(materialId: number): Observable<StandardResponse> {
    return this.http.delete<StandardResponse>(
      `${this.apiUrl}/kurse/material-delete.php?id=${materialId}`
    );
  }
}
